import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { CapsuleDesignActor } from '~/scenes/Build/scenes/FlowBuilder/CapsuleDesign/CapsuleDesign.machine';
import { TestCaseActor } from '~/scenes/Build/scenes/FlowBuilder/components/Design/machines/TestCase.machine';
import { DebuggerActor } from '~/scenes/Build/scenes/FlowBuilder/components/Design/panels/Debugger/Debugger.machine';
import { FlowCanvasMachine } from '~/scenes/Build/scenes/FlowBuilder/components/FlowCanvas/FlowCanvas.machine';

export type MachineActorsState = {
  debuggerActor: DebuggerActor;
  testCaseActor: TestCaseActor;
  flowCanvasActor: FlowCanvasMachine.Actor;
  capsuleActor: CapsuleDesignActor;
};

export type MachineActorsActions = {
  setDebuggerActor: (actor: DebuggerActor) => void;
  setTestCaseActor: (actor: TestCaseActor) => void;
  setFlowCanvasActor: (actor: FlowCanvasMachine.Actor) => void;
  setCapsuleActor: (actor: CapsuleDesignActor) => void;
  reset: () => void;
};

export type MachineActorsStore = MachineActorsState & MachineActorsActions;

const createDummySnapshot = () => ({
  value: {},
  context: {},
  matches: () => false,
  can: () => false,
  hasTag: () => false,
  toJSON: () => ({})
});

const createDummyActor = () =>
  ({
    id: '',
    send: () => {},
    subscribe: (observer: any) => {
      const fn = typeof observer === 'function' ? observer : observer?.next;
      fn?.(createDummySnapshot());
      return { unsubscribe: () => {} };
    },
    getSnapshot: createDummySnapshot,
    start: () => {},
    stop: () => {}
  }) as unknown;

const initialState: MachineActorsState = {
  debuggerActor: createDummyActor() as DebuggerActor,
  testCaseActor: createDummyActor() as TestCaseActor,
  flowCanvasActor: createDummyActor() as FlowCanvasMachine.Actor,
  capsuleActor: createDummyActor() as CapsuleDesignActor
};

export const useMachineActorsStore = create<MachineActorsStore>()(
  devtools(set => ({
    ...initialState,

    setDebuggerActor: actor => set({ debuggerActor: actor }),
    setTestCaseActor: actor => set({ testCaseActor: actor }),
    setFlowCanvasActor: actor => set({ flowCanvasActor: actor }),
    setCapsuleActor: actor => set({ capsuleActor: actor }),

    reset: () =>
      set({
        debuggerActor: createDummyActor() as DebuggerActor,
        testCaseActor: createDummyActor() as TestCaseActor,
        flowCanvasActor: createDummyActor() as FlowCanvasMachine.Actor,
        capsuleActor: createDummyActor() as CapsuleDesignActor
      })
  }))
);
